import { useEffect, useState, useMemo, useRef } from 'react';
import { useStore } from '@/store';
import service from '@/request/service';
import { getQueryStringByName, isShortPage } from '@/utils';
import { Toast } from '@/components/Toast';
import { ALERT_TYPE, GOP_BIZCODE, ROUTER_MAP } from '@/types';
import { getIntl } from '@/locales';
import getFaceBook from '@/utils/facebook';

export const useInit = () => {
  const { orderObj, baseObj, setOrderObj, setBaseObj, routerHistory } = useStore();
  const [webcanShow, setWebCanShow] = useState(false);
  const [firstSeatShow, setFirstSeatShow] = useState(false);
  const [updateNum, setUpdateNum] = useState(0);
  const intl = getIntl();

  const innerOrderObj = useRef<any>(orderObj);
  const innerBaseObj = useRef<any>(baseObj);
  useEffect(() => {
    if (routerHistory.length === 0) {
      return;
    }
    if (routerHistory.length === 1 && routerHistory[0] === ROUTER_MAP.SEAT) {
      Promise.all([
        service.getScheduleInfo({
          scheduleId: getQueryStringByName('scheduleId'),
          cinemaId: getQueryStringByName('cinemaId'),
        }),
        service.getScreen(),
      ]).then(([scheduleRes, screenRes]) => {
        if (scheduleRes.result.bizCode !== GOP_BIZCODE.SUCCESS || screenRes.result.bizCode !== GOP_BIZCODE.SUCCESS) {
          return;
        }
        // 需要新增一个获取场次接口；看看这个场次有没有卖品信息

        const showTimesScheduleList = screenRes.result.bizValue?.films ?? [];
        const scheduleId = getQueryStringByName('scheduleId');
        // 从 dates 数组中查找对应的场次数据
        let targetSchedule: any = null;
        for (const film of showTimesScheduleList) {
          if (film.dates) {
            for (const dateItem of film.dates) {
              const schedule = dateItem.schedules?.find((s) => s.scheduleId === scheduleId);
              if (schedule) {
                targetSchedule = schedule;
                break;
              }
            }
          }
          if (targetSchedule) break;
        }

        const { film, ...screen } = scheduleRes.result.bizValue;
        innerOrderObj.current.schedule = {
          ...innerOrderObj.current.schedule,
          ...screen,
          ...(targetSchedule ?? {}),
          endTime: new Date(screen?.endTime || new Date()).getTime() + 24 * 60 * 60 * 1000, // 给24小时,
        };
        innerOrderObj.current.film = film;

        if (!film.filmId) {
          innerOrderObj.current.film.filmId = film.filmLinkId;
        }

        setOrderObj(innerOrderObj.current);

        setTimeout(() => {
          setFirstSeatShow(true);
        }, 300);
      });
    } else {
      setFirstSeatShow(true);
    }
  }, [routerHistory]);
  // 检查 登陆方式区号等配置
  const getLoginMethod = async (props) => {
    const { can, needReturn = false } = props;
    if (!can) return;
    const { loginMethods = [], expirationTime } = innerBaseObj.current.loginInfo || {};
    const canNext = loginMethods?.length && Boolean(expirationTime) && expirationTime > new Date().getTime();
    if (!canNext) {
      if (needReturn) {
        return false;
      }
      service
        .getLoginMethod({
          leaseCode: getQueryStringByName('wapid'),
        })
        .then((res) => {
          innerBaseObj.current.loginInfo = {
            ...res?.result?.bizValue,
            expirationTime: new Date().getTime() + 1000 * 60 * 60,
          };
          setBaseObj(baseObj);
        });
    }

    return true;
  };

  // 检查渠道
  const checkCannelCode = (props) => {
    const { changeCan, needReturn = false } = props;
    const wapid = getQueryStringByName('wapid');
    if (!wapid && !innerBaseObj.current.wapid) {
      if (needReturn) {
        return false;
      }
      changeCan(false);
      Toast({
        type: ALERT_TYPE.CONFIRM,
        content: intl.formatMessage({ id: 'iWeb.hooks.useInit.TheChannelIsNotClear', defaultMessage: '渠道未明确' }),
      });
    }
    innerBaseObj.current.wapid = wapid;
    return true;
  };

  // 检查影院
  const checkCinema = async (props) => {
    const { can, changeCan, needReturn = false } = props;
    if (!can) return;
    // 需要有影院
    if (!innerBaseObj.current.cinema.cinemaLinkId) {
      if (needReturn) {
        return false;
      }
      const linkid = getQueryStringByName('cinemaId');
      changeCan(false);
      service.getCinemas().then((res) => {
        const cities = res.result?.bizValue?.cities?.map((item) => {
          const { cinemas, ...others } = item;
          return item.cinemas.map((ii) => {
            return {
              ...ii,
              ...others,
            };
          });
        });
        const cinemaList = cities?.flat(1);
        const cinema = cinemaList?.find((item) => item.cinemaLinkId == linkid);
        // 有id 且能找到 就设置
        if (cinema && linkid) {
          innerBaseObj.current.cinema = cinema;
          innerBaseObj.current.location = {
            id: cinema.cityCode,
            name: cinema.cityName,
          };
          setUpdateNum(updateNum + 1);
        }
        // 无id 且 有影院 选第一个
        if (!linkid && cinemaList.length) {
          innerBaseObj.current.cinema = cinemaList[0];
          innerBaseObj.current.location = {
            id: cinemaList[0].cityCode,
            name: cinemaList[0].cityName,
          };
          setUpdateNum(updateNum + 1);
        }
        // 有id 且找不到对应的影院
        if (linkid && !cinema) {
          Toast({
            type: ALERT_TYPE.CONFIRM,
            content: intl.formatMessage({
              id: 'iWeb.hooks.useInit.TheCinemaDoesNotExist',
              defaultMessage: '该影院不存在',
            }),
          });
        }
      });
    }

    return true;
  };

  // 检查货币
  const checkCurrencySign = (props) => {
    const { can, needReturn = false } = props;
    if (!can) return;
    if (!innerOrderObj.current.currencySign) {
      if (needReturn) {
        return false;
      }
      service
        .getCinemaCurrencySign({
          channelCode: baseObj.wapid,
          cinemaLinkId: baseObj.cinema.cinemaLinkId,
        })
        .then((res) => {
          // console.log('[dev]看看-res:', res);
          const currencySign = res?.result?.bizValue?.filter((item) => {
            return item.cinemaLinkId == innerBaseObj.current.cinema.cinemaLinkId;
          })[0];
          // console.log('[dev]看看-currencySign:', currencySign);
          if (currencySign) {
            innerOrderObj.current.currencySign = currencySign;
          } else {
            innerOrderObj.current.currencySign = res?.result?.bizValue?.[0];
          }
        });
    }

    return true;
  };

  // 渠道配置
  // const getChannelSetting = async (props) => {
  //   const { can, needReturn = false } = props;
  //   if (!can) return;
  //   if (true) {
  //     if (needReturn) {
  //       return false;
  //     }
  //     try {
  //       service
  //         .getChannelSetting()
  //         .then((res) => {
  //           const setting = res?.result?.bizValue ? JSON.parse(res?.result?.bizValue) : {};
  //           console.log('log_[res?.result?.bizValue]==>', setting);
  //           innerOrderObj.current.channelSetting = setting;
  //         })
  //         .catch(
  //           // @ts-ignore
  //           // 防止本地或者意外报错，其他走不动
  //           (innerOrderObj.current.channelSetting = true),
  //         );
  //     } catch (error) {
  //       // console.log('[dev]看看-error:', error);
  //       // @ts-ignore
  //       // 防止本地或者意外报错，其他走不动
  //       innerOrderObj.current.channelSetting = true;
  //     }
  //   }
  //   return true;
  // };

  /**
   * 挂载apple登录对象
   */
  const initAppleID = () => {
    (function (d, s, id) {
      let js,
        fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      js = d.createElement(s);
      js.id = id;
      js.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'appleIda-jssdk');
    let appleScriptEl = document.getElementById('appleIda-jssdk');
    if (appleScriptEl) {
      appleScriptEl.onload = function () {
        let client = baseObj?.loginInfo.clientIds?.find((item) => item.loginType === 'APPLE') || {};
        const AppleIDParam = {
          clientId: client?.clientId || 'ai.icirena.site-id',
          scope: 'openid name email',
          redirectURI: 'https://pre-site.icirena.ai/login/index.html?wapid=HGZYYZZH_H5_PROD_S_MPS',
          state: '1234',
          usePopup: true,
        };
        AppleID?.auth.init(AppleIDParam);
      };
    }
  };
  /**
   * 初始化facebook
   */
  const initFaceBook = () => {
    getFaceBook({ baseObj });
  };

  // const { canShow: MpayCanShow } = checkMpayLogin({ webcanShow });

  const init = async () => {
    let can = true;
    // 需要有渠道
    checkCannelCode({
      can,
      changeCan: (val) => {
        can = val;
      },
    });

    // 需要有影院id
    checkCinema({
      can,
      changeCan: (val) => {
        can = val;
      },
    });

    // // 需要有货币接口
    checkCurrencySign({
      can,
      changeCan: (val) => {
        can = val;
      },
    });

    // 渠道设置,
    // getChannelSetting({
    //   can,
    //   changeCan: (val) => {
    //     can = val;
    //   },
    // });

    // 获取登陆方式
    getLoginMethod({
      can,
      changeCan: (val) => {
        can = val;
      },
    });
    // 第三方登录
    initAppleID();
    initFaceBook();
    if (can) {
      asyncCheck();
    }
  };

  const asyncCheck = () => {
    const res1 = getLoginMethod({ can: true, needReturn: true });
    const res2 = checkCannelCode({ can: true, needReturn: true });
    const res3 = checkCinema({ can: true, needReturn: true });
    const res4 = checkCurrencySign({ can: true, needReturn: true });
    // 目前没有调用
    // const res5 = getChannelSetting({ can: true, needReturn: true });
    // // console.log('[dev]看看-asyncCheck:', !!res1, !!res2, !!res3, !!res4, !!res5);
    if (!!res1 && !!res2 && !!res3 && !!res4) {
      setOrderObj(innerOrderObj.current);
      setBaseObj(innerBaseObj.current);
      setWebCanShow(true);
    } else {
      setTimeout(() => {
        asyncCheck();
      }, 500);
    }
  };

  useEffect(() => {
    if (!isShortPage()) {
      init();
    }
  }, [updateNum]);

  const setHtmlAttribute = (locale) => {
    const htmlEl = document.querySelector('html');
    htmlEl?.setAttribute('lang', locale || 'zh-TW');
  };

  useEffect(() => {
    setHtmlAttribute(baseObj.currentLanguage);
  }, []);

  const canShow = useMemo(() => {
    return webcanShow && firstSeatShow;
  }, [webcanShow, firstSeatShow]);

  // return webcanShow
  return canShow;
};
